<template>
  <div id="system-status">
    <h1>System Status</h1>
    <p v-if="statusPageError">
      Status is currently unavailable.
    </p>
    <template v-else>
      <system-status-component-bars :components="components"/>
      <a
        class="button"
        href="https://qvalent.statuspage.io"
        target="_blank"
        rel="noopener noreferrer"
      >
        View Detailed Status
      </a>

      <system-status-incidents
        :incidents="maintenance"
        no-incidents-text="No maintenance is currently scheduled."
        title="Scheduled Maintenance"
      />
    </template>
  </div>
</template>

<script>
import SystemStatusComponentBars from '@/components/SystemStatusComponentBars';
import SystemStatusIncidents from '@/components/SystemStatusIncidents';
import axios from 'axios';
import moment from 'moment-mini';

export default {
  components: { SystemStatusComponentBars, SystemStatusIncidents },
  props: {
    // This prop is for unit testing
    currentMoment: {
      default: () => moment(),
      type: Object
    }
  },
  data() {
    return {
      statusPageError: false,
      components: [],
      maintenance: []
    };
  },
  created() {
    axios.get('https://qvalent.statuspage.io/index.json')
      .then(response => {
        // Get the components (systems) we want for the component bars
        this.components = response.data.components
          .filter(component => component.name !== 'Invoice Finance');

        /**
         * Incident types:
         *   - Maintenance:
         *     - Impact values: 'maintenance'
         *     - Status values: 'scheduled', 'in_progress', 'verifying' and 'completed'
         *   - Incidents:
         *     - Impact values: 'none', 'minor', 'major' and 'critical'
         *     - Status values: 'investigating', 'identified', 'monitoring' and 'resolved'
         *
         * We only show maintenance since any important incidents will change the color
         * of the status bars and the user can click to go to the main statuspage site.
         */
        this.maintenance = response.data.incidents
          .filter(incident => incident.impact === 'maintenance')
          // Make sure incidents which are accidentally entered as maintenance don't show up
          .filter(incident => ['completed', 'scheduled', 'verifying', 'in_progress']
            .includes(incident.status))
          // Only show scheduled maintenance within the next two weeks
          .filter(incident => incident.status !== 'scheduled'
            || (this.daysFromDate(incident.scheduled_for) >= 0
              && this.daysFromDate(incident.scheduled_for) <= 14))
          // Only show completed maintenance within the last week
          .filter(incident => incident.status !== 'completed'
            || (this.daysFromDate(incident.resolved_at) <= 0
              && this.daysFromDate(incident.resolved_at) >= -7))
          .sort((a, b) => {
            // Order by status first
            if (a.status !== b.status) {
              return this.getMaintenanceDisplayPriority(a) < this.getMaintenanceDisplayPriority(b)
                ? 1 : -1;
            }
            // And by most recently resolved second
            return new Date(b.resolved_at).getTime() - new Date(a.resolved_at).getTime();
          });
      })
      .catch(() => (this.statusPageError = true));
  },
  methods: {
    daysFromDate(date) {
      return moment(date).diff(this.currentMoment, 'days');
    },
    getMaintenanceDisplayPriority(maintenance) {
      // Higher value means higher priority
      const status = {
        in_progress: 4,
        verifying: 3,
        scheduled: 2,
        completed: 1
      };
      return status[maintenance.status];
    }
  }
};
</script>

<style lang="scss">
  #system-status {
    > :last-child {
      margin-bottom: 50px;
    }

    .button {
      margin-left: calc(50% - 140px);
    }
  }
</style>
